import type { ReactElement } from 'react';
import { Tooltip } from '../Tooltip';

/**
 * A column header's own explanation — `ColumnDef.headerTooltip`, on hover and on focus.
 *
 * <b>Written once because three tables draw their own headers</b> — `<IndexTable>`,
 * `<PlantillaIndexTable>` and `<StockLevelsIndexTable>` — and a prop honoured by one of them is
 * a prop that silently does nothing on the next table somebody declares it on.
 *
 * <b>`block` is not decoration here.</b> The tooltip anchor is `inline-flex` by default, and a
 * header's label is a full-width thing: a sortable header is `w-full` so its glyph sits on the
 * column's outer right, and a right-aligned one is `text-right`. Inside an inline-flex anchor
 * each would shrink to its text and the glyphs would stop lining up down their columns. A
 * header cell is the full-width context the rule names.
 *
 * <b>Nothing is wrapped where there is nothing to say</b> — a column with no tooltip renders
 * exactly the markup it rendered before, which is what keeps a table of twenty-five headers
 * from drawing twenty-five anchors nobody can hover anything out of.
 */
export function HeaderTooltip({
  tooltip,
  children,
}: {
  tooltip?: string;
  /** The label itself, so `aria-describedby` lands on the control a reader focuses. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
}) {
  if (!tooltip) return children;

  return (
    <Tooltip label={tooltip} block>
      {children}
    </Tooltip>
  );
}
